import { ReactNode } from 'react';

import { IconProps } from '../icon';

export type FormInputSize = 'small' | 'regular' | 'large';
export type OptionValue = string | number;

export type Option<T extends OptionValue = string> = {
  value: T;
  label: string;
  title?: string;
  disabled?: boolean;
  description?: string;
  icon?: IconProps['name'];
  badge?: string;
  tooltip?: ReactNode;
};

export type OptionGroup<T extends OptionValue = string> = {
  key: string;
  label: string;
  options: Option<T>[];
};

export type SelectOption<T extends OptionValue = string> = Option<T> | OptionGroup<T>;

export type SelectOptions<T extends OptionValue = string> = SelectOption<T>[];

export const isOptionGroup = <T extends SelectOption<OptionValue>>(
  item: T,
): item is Extract<T, { options: unknown[] }> => 'options' in item;

export const isOption = <T extends SelectOption<OptionValue>>(
  item: T,
): item is Extract<T, { value: unknown }> => !isOptionGroup(item);

export const flattenSelectOptions = <T extends OptionValue>(
  options: SelectOptions<T> | Readonly<SelectOptions<T>>,
): Option<T>[] => options.flatMap((opt) => (isOptionGroup(opt) ? opt.options : [opt]));

/**
 * Whether a value resolves to an option, at the top level or within any group.
 */
export const selectOptionExists = <T extends OptionValue>(
  options: SelectOptions<T> | Readonly<SelectOptions<T>>,
  value: T,
): boolean => flattenSelectOptions(options).some((option) => option.value === value);

/**
 * Convenience function for getting a single Option item out of a SelectOptions output where the item might be nested within a group.
 */
export const getSelectedOption = <T extends OptionValue>(
  selection: SelectOption<T> | SelectOptions<T>,
): [Option<T>, OptionGroup<T> | undefined] => {
  if (!Array.isArray(selection)) {
    return isOptionGroup(selection) ? [selection.options[0], selection] : [selection, undefined];
  }
  if (selection.length > 1) {
    throw new Error('Multiple options selected');
  }
  const first = selection.at(0);
  if (first === undefined) {
    throw new Error('No option selected');
  }
  if (isOptionGroup(first)) {
    const [option, _subGroup] = getSelectedOption(first.options);
    return [option, first];
  }
  return [first, undefined];
};

/**
 * Filter options based on a predicate function. The predicate receives each option and its parent group (if any).
 */
export const filterOptions = <
  T extends OptionValue,
  O extends Option<T> = Option<T>,
  OG extends OptionGroup<T> = OptionGroup<T>,
>(
  options: SelectOptions<T> | Readonly<SelectOptions<T>>,
  predicate: (option: O, group: OG | null) => boolean,
): SelectOptions<T> =>
  options
    .map((opt) => {
      if (isOptionGroup(opt)) {
        const filteredOptions = opt.options.filter((o) => predicate(o as O, opt as OG));
        return filteredOptions.length > 0 ? { ...opt, options: filteredOptions } : null;
      }
      return predicate(opt as O, null) ? opt : null;
    })
    .filter((opt): opt is O | OG => opt !== null);

/**
 * Add values to options if they don't exist. Missing values are added to the beginning of the options array.
 */
export const padSelectOptions = <T extends OptionValue>(
  options: SelectOptions<T> | Readonly<SelectOptions<T>>,
  values: SelectOptions<T>,
): SelectOptions<T> => {
  const missingValues = new Set(
    values.reduce<Option<T>[]>((acc, v) => {
      if (isOptionGroup(v)) {
        return [
          ...acc,
          ...v.options.filter(
            (o) =>
              !options.some(
                (opt) =>
                  isOptionGroup(opt) &&
                  opt.key === v.key &&
                  opt.options.some((o2) => o2.value === o.value),
              ),
          ),
        ];
      }
      return options.some((opt) => !isOptionGroup(opt) && opt.value === v.value)
        ? acc
        : [...acc, v];
    }, []),
  );
  return [...Array.from(missingValues), ...options];
};

/**
 * Remove values from options if they exist. A unique value is defined by their `value` property and their parent group path in the options tree.
 * Empty groups resulting from the removal are also removed from the options.
 */
export const removeSelectOptions = <T extends OptionValue>(
  options: SelectOptions<T> | Readonly<SelectOptions<T>>,
  values: SelectOptions<T>,
): SelectOptions<T> => {
  const flatValuesToRemove = values.filter(isOption);
  const groupValuesToRemove = values.filter(isOptionGroup);

  return [...options].reduce<SelectOptions<T>>((acc, opt) => {
    if (isOptionGroup(opt)) {
      const matchingGroup = groupValuesToRemove.find((g) => g.key === opt.key);
      if (matchingGroup === undefined) {
        return [...acc, opt];
      }
      const remaining = removeSelectOptions(opt.options, matchingGroup.options);
      return remaining.length > 0 ? [...acc, { ...opt, options: remaining as Option<T>[] }] : acc;
    }
    return flatValuesToRemove.some((v) => v.value === opt.value) ? acc : [...acc, opt];
  }, []);
};

/**
 * Add new values to options if they don't exist. A unique value is defined by their `value` property and their parent group path in the options tree.
 * Parent groups of the added options are also added if they don't exist in the options.
 */
export const addSelectOptions = <T extends OptionValue>(
  options: SelectOptions<T> | Readonly<SelectOptions<T>>,
  values: SelectOptions<T>,
): SelectOptions<T> => {
  const flatValuesToAdd = values.filter(isOption);
  const groupValuesToAdd = values.filter(isOptionGroup);

  const withMergedGroups = options.map((opt) => {
    if (!isOptionGroup(opt)) {
      return opt;
    }
    const matchingGroup = groupValuesToAdd.find((g) => g.key === opt.key);
    if (matchingGroup === undefined) {
      return opt;
    }
    return {
      ...opt,
      options: addSelectOptions(opt.options, matchingGroup.options) as Option<T>[],
    };
  });

  const existingGroupValues = new Set(options.flatMap((o) => (isOptionGroup(o) ? [o.key] : [])));
  const newGroups = groupValuesToAdd.filter((g) => !existingGroupValues.has(g.key));

  const existingFlatValues = new Set(options.flatMap((o) => (isOption(o) ? [o.value] : [])));
  const newFlatOptions = flatValuesToAdd.filter((o) => !existingFlatValues.has(o.value));

  return [...withMergedGroups, ...newGroups, ...newFlatOptions];
};

/**
 * Add new values to options if they don't exist, or remove them if they do exist. A unique value is defined by their `value` property and their parent group path in the options tree.
 * Parent groups of the added options are also added if they don't exist in the options. Empty groups resulting from the removal are also removed from the options.
 */
export const toggleSelectOptions = <T extends OptionValue>(
  options: SelectOptions<T> | Readonly<SelectOptions<T>>,
  values: SelectOptions<T>,
): SelectOptions<T> =>
  values.reduce<SelectOptions<T>>(
    (acc, value) => {
      if (isOptionGroup(value)) {
        const existingGroup = acc.find((opt) => isOptionGroup(opt) && opt.key === value.key);
        if (existingGroup === undefined || !isOptionGroup(existingGroup)) {
          return addSelectOptions(acc, [value]);
        }
        const toggledOptions = toggleSelectOptions(
          existingGroup.options,
          value.options,
        ) as Option<T>[];
        if (toggledOptions.length === 0) {
          return acc.filter((opt) => !(isOptionGroup(opt) && opt.key === existingGroup.key));
        }
        return acc.map((opt) =>
          isOptionGroup(opt) && opt.key === existingGroup.key
            ? { ...existingGroup, options: toggledOptions }
            : opt,
        );
      }
      const exists = acc.some((opt) => isOption(opt) && opt.value === value.value);
      return exists ? removeSelectOptions(acc, [value]) : addSelectOptions(acc, [value]);
    },
    [...options],
  );
